/* Capacidades (Painel Administrativo) — o que a IA pode fazer, por empresa.
 *
 * Organizado por consequência, não por categoria: as que agem dentro do
 * atendimento e as que ESCREVEM PARA FORA (mandam mensagem a terceiros). As do
 * segundo grupo só ligam depois de dizer PARA QUEM — a API recusa salvar sem
 * destino. Ligada ao real `/api/tools`, `/api/assistants` e o PUT de tools.
 * Nebula. */

import React, { useState } from 'react';

import { api } from '../api/cliente';
import { useRecurso } from '../estado/recurso';
import { Aviso, Button, card, Cabecalho, Secao, Tag } from './ui';

const ROTULO_PAPEL = { instrutor: 'Instrutor bíblico', pastor: 'Pastor', permitidos: 'Números que podem receber' };
const EXEMPLO = { instrutor: '5511999998888', pastor: '5511999997777', permitidos: '5511999998888, 5511999996666' };

const paraLista = (t) => (t || '').split(/[\s,]+/).filter(Boolean);
const paraTexto = (l) => (l || []).join(', ');

export default function Capacidades() {
  const recurso = useRecurso(
    (sinal) => Promise.all([api.assistentes(sinal), api.catalogoDeTools(sinal)]).then(([e, c]) => ({ empresas: e?.itens || [], catalogo: c?.itens || [] })),
    [],
  );
  const empresas = recurso.dados?.empresas || [];
  const catalogo = recurso.dados?.catalogo || [];
  const [selId, setSelId] = useState(null);
  const selecionada = empresas.find((e) => e.id === selId) || null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Cabecalho titulo="Capacidades" descricao="O que a Sofia pode fazer em cada empresa. As que escrevem para fora só ligam com destino." />
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,300px) minmax(0,1fr)', gap: 20, flex: 1, minHeight: 0, padding: '0 24px 24px' }}>
        <Secao titulo={recurso.carregando ? 'Carregando…' : 'Empresas'} rolar>
          {recurso.erro ? (
            <Aviso titulo="Não carregou" texto={recurso.erro.mensagem} acao={recurso.erro.temporario ? 'Tentar de novo' : null} aoAcao={recurso.recarregar} />
          ) : (
            (empresas).map((e) => {
              const ativa = e.id === selId;
              return (
                <button key={e.id} type="button" onClick={() => setSelId(e.id)} style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', padding: '13px 20px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: ativa ? 'var(--surface-active)' : 'transparent', boxShadow: ativa ? 'inset 3px 0 0 var(--accent)' : 'none', color: 'var(--text-primary)', font: 'var(--type-body-strong)', fontSize: 'var(--fs-body-sm)' }}>
                  {e.nome}
                  <span style={{ display: 'block', font: 'var(--type-caption)', color: 'var(--text-muted)', fontWeight: 400 }}>{(e.tools || []).length} capacidade(s) ligada(s)</span>
                </button>
              );
            })
          )}
        </Secao>

        {selecionada ? (
          <Detalhe key={selecionada.id} empresa={selecionada} catalogo={catalogo} aoSalvar={recurso.recarregar} />
        ) : (
          <section style={{ ...card, alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>Selecione uma empresa para configurar as capacidades.</span>
          </section>
        )}
      </div>
    </div>
  );
}

function Detalhe({ empresa, catalogo, aoSalvar }) {
  const [ligadas, setLigadas] = useState(() => empresa.tools || []);
  const [destinos, setDestinos] = useState(() => empresa.tool_config || {});
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);

  const dentro = catalogo.filter((t) => !t.escreve_para_fora);
  const fora = catalogo.filter((t) => t.escreve_para_fora);

  const alterna = (nome) => setLigadas((l) => (l.includes(nome) ? l.filter((n) => n !== nome) : [...l, nome]));
  const setDestino = (tool, papel, texto) => setDestinos((d) => ({ ...d, [tool]: { ...(d[tool] || {}), [papel]: paraLista(texto) } }));

  // Ligadas que escrevem para fora e ainda estão sem nenhum destino.
  const semDestino = fora
    .filter((t) => ligadas.includes(t.nome))
    .filter((t) => !(t.papeis || []).some((p) => ((destinos[t.nome] || {})[p] || []).length));

  async function salvar() {
    setSalvando(true);
    try {
      const limpo = Object.fromEntries(Object.entries(destinos).filter(([tool]) => ligadas.includes(tool)));
      await api.salvarTools(empresa.id, ligadas, limpo);
      setSalvo(true);
      aoSalvar?.();
      setTimeout(() => setSalvo(false), 2200);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section style={{ ...card, overflow: 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, padding: '20px 22px 26px' }}>
        <Grupo titulo="Durante o atendimento" descricao="Agem dentro da conversa, com revisão implícita. Ligar é seguro.">
          {dentro.map((t) => <ItemTool key={t.nome} tool={t} ligada={ligadas.includes(t.nome)} aoAlternar={() => alterna(t.nome)} />)}
        </Grupo>

        <Grupo titulo="Escrevem para fora" descricao="Mandam mensagem a alguém que não pediu, sem revisão. Cada uma só liga com destino.">
          {fora.map((t) => {
            const ligada = ligadas.includes(t.nome);
            const faltaDestino = ligada && !(t.papeis || []).some((p) => ((destinos[t.nome] || {})[p] || []).length);
            return (
              <div key={t.nome} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <ItemTool tool={t} ligada={ligada} aoAlternar={() => alterna(t.nome)} alerta={faltaDestino ? 'sem destino — não será salva' : null} />
                {ligada && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '0 0 4px 34px' }}>
                    {(t.papeis || []).map((p) => (
                      <label key={p} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{ROTULO_PAPEL[p] || p}</span>
                        <input value={paraTexto((destinos[t.nome] || {})[p])} onChange={(e) => setDestino(t.nome, p, e.target.value)} placeholder={EXEMPLO[p]} style={{ height: 36, padding: '0 10px', background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', color: 'var(--text-primary)', font: 'var(--type-body)', fontSize: 'var(--fs-body-sm)', outline: 'none' }} />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </Grupo>
      </div>

      <footer style={{ position: 'sticky', bottom: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '14px 22px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>
        <Button variant="primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando…' : 'Salvar capacidades'}</Button>
        {semDestino.length > 0 && <span style={{ font: 'var(--type-caption)', color: 'var(--warning-text)' }}>{semDestino.length} capacidade(s) sem destino não serão salvas</span>}
        {salvo && <span style={{ marginLeft: 'auto', font: 'var(--type-caption)', color: 'var(--success-text)' }}>Salvo</span>}
      </footer>
    </section>
  );
}

function Grupo({ titulo, descricao, children }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <h4 style={{ margin: 0, font: 'var(--type-label)', fontWeight: 'var(--fw-semibold)', color: 'var(--text-muted)', letterSpacing: 'var(--ls-caps)', textTransform: 'uppercase' }}>{titulo}</h4>
        <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{descricao}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
    </section>
  );
}

function ItemTool({ tool, ligada, aoAlternar, alerta }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      <button type="button" role="switch" aria-checked={ligada} onClick={aoAlternar} style={{ flex: 'none', marginTop: 2, width: 38, height: 22, borderRadius: 'var(--radius-pill)', border: 0, cursor: 'pointer', background: ligada ? 'var(--accent)' : 'var(--border-strong)', position: 'relative', transition: 'background var(--dur-fast) var(--ease-standard)' }}>
        <span style={{ position: 'absolute', top: 2, left: ligada ? 18 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left var(--dur-fast) var(--ease-standard)' }} />
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, font: 'var(--type-body-strong)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-primary)' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-label)' }}>{tool.nome}</span>
          {alerta && <Tag tone="warning">{alerta}</Tag>}
        </span>
        <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{tool.consequencia || tool.descricao}</span>
      </div>
    </div>
  );
}
